/**
 * UIScreenBridge — the behaviour ↔ screen seam behind `engine.ui.getScreen()` (UI-9).
 *
 * One bridge per UICanvas GameObject, owned by the host UI subsystem and KEYED BY GO — it
 * survives canvas remounts (hot-reload re-creates the Vue app; the bridge's reactive props
 * re-apply automatically because the app renders THROUGH them, see createUIApp's wrapper).
 *
 * - `setProps(patch)` shallow-merges into a `shallowReactive` object the root component
 *   receives as props — Vue reactivity propagates like any parent-driven prop change.
 * - `on(event, fn)` subscribes to emits from the .uix ROOT (`defineEmits`/`emit('menu')`):
 *   the bridge installs ONE stable dispatcher per event under Vue's handler key
 *   (`menu` → `onMenu`). Vue clones reactive vnode props (guardReactiveProps), so the FIRST
 *   subscribe per event becomes visible after one scheduler flush (a microtask) — subscribe
 *   in onStart and events land from the next frame on; further subscribes are immediate.
 */
import { shallowReactive } from '@vue/runtime-core';

export type UIScreenEventHandler = (...args: unknown[]) => void;

export class UIScreenBridge {
  /** Reactive root props — pass to createUIApp; mutate only through setProps. */
  readonly props: Record<string, unknown> = shallowReactive({});

  private readonly handlers = new Map<string, Set<UIScreenEventHandler>>();

  constructor(private readonly onError: (event: string, error: unknown) => void = defaultError) {}

  setProps(patch: Record<string, unknown>): void {
    if (patch && typeof patch === 'object') Object.assign(this.props, patch);
  }

  on(event: string, handler: UIScreenEventHandler): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
      // Vue toHandlerKey convention: emit('menu') looks up rootProps.onMenu
      const key = `on${event[0]?.toUpperCase() ?? ''}${event.slice(1)}`;
      const sink = set;
      this.props[key] = (...args: unknown[]) => {
        for (const fn of [...sink]) {
          try {
            fn(...args);
          } catch (e) {
            this.onError(event, e);
          }
        }
      };
    }
    set.add(handler);
    return () => set!.delete(handler);
  }
}

function defaultError(event: string, error: unknown): void {
  console.error(`[ui] screen '${event}' handler failed:`, error);
}
